import {
  Address,
  AloftArea,
  AloftPrice,
  ZillowPropertySearchResponse,
  isDefined,
  isNull,
  isUndefined,
  orUndefined,
  stringToAddress,
  syntaxFNMA,
} from '@aloft/toolkit-shared';

import { proximityMiles } from '../../../utils/proximityMiles.ts';
import { Coordinates } from '../../map/types.ts';
import { CompSelectionProperty } from '../types.ts';
import { addQualityTiers } from './addQualityTiers.ts';
import { NewSimilarityScoreCalcOptions, addSimilarityScores } from './addSimilarityScores.ts';
import { getMonthFromUnix } from './getMonthFromUnix.ts';
import { getZillowCompRecID } from './getZillowCompRecID.ts';

export const zillowPropsToCompSelectionProps = (
  subjectCoordinates: Coordinates | undefined,
  subject: CompSelectionProperty,
  comps: CompSelectionProperty[],
  listings: ZillowPropertySearchResponse[],
  newCalcOptions?: NewSimilarityScoreCalcOptions,
): CompSelectionProperty[] => {
  // we don't want duplicate CompRecIDs in the result set, which is very
  // possible given how they are defined. so, for each CompRecID in the input
  // dataset, we pick one listing for it. The effect of this reduce will just be
  // that whichever listing appears later in the input will be selected for
  // that CompRecID
  const dedupe = listings.reduce<Map<string, ZillowPropertySearchResponse>>((acc, listing) => {
    const id = getZillowCompRecID(listing);
    if (isDefined(id)) acc.set(id, listing);
    return acc;
  }, new Map());

  const properties = Array.from(dedupe.values())
    .map((listing: ZillowPropertySearchResponse) => {
      const saleMonth = getMonthFromUnix(listing.dateSold);
      const id = getZillowCompRecID(listing);
      if (isUndefined(saleMonth) || isUndefined(id)) {
        return undefined;
      }
      const saleDate = orUndefined(syntaxFNMA.isoMonthToAloftDate)(saleMonth);
      const address: Address = stringToAddress(listing.address ?? '');
      if (isUndefined(address.AddressLineText)) {
        return undefined;
      }

      const subjectLatLong = isDefined(subjectCoordinates)
        ? { longitude: subjectCoordinates.lng, latitude: subjectCoordinates.lat }
        : undefined;
      const listingLatLong =
        !isNull(listing.latitude) && !isNull(listing.longitude)
          ? { longitude: listing.longitude, latitude: listing.latitude }
          : undefined;

      const distanceToSubject =
        isDefined(subjectLatLong) && isDefined(listingLatLong)
          ? +(proximityMiles(subjectLatLong, listingLatLong)?.toFixed(2) ?? 0)
          : undefined;

      const t = comps.find((c) => c.listingID === id)?.t ?? 'z';

      const result: CompSelectionProperty = {
        t,
        id,
        address,
        gla: isNull(listing.livingArea)
          ? undefined
          : AloftArea.fromNumber(listing.livingArea, 'sqft'),
        lotSize: getLotAreaInSqft(listing),
        saleDate,
        salePrice: AloftPrice.fromDollarLike(listing.price) ?? undefined,
        distanceToSubject,
        imageUrls: listing.imgSrc ? [listing.imgSrc] : undefined,
        latitude: listing.latitude ? listing.latitude : undefined,
        longitude: listing.longitude ? listing.longitude : undefined,
        fullBaths: !isNull(listing.bathrooms) ? listing.bathrooms : undefined,
        halfBaths: undefined,
        bedroomCount: !isNull(listing.bedrooms) ? listing.bedrooms : undefined,
        yearBuilt: undefined,
        bedrooms: !isNull(listing.bedrooms) ? listing.bedrooms : undefined,
        quality: undefined,
        condition: undefined,
        listingID: undefined,
        mlsStatus: listing.listingStatus ?? undefined,
      };

      return result;
    })
    .filter(isDefined);

  addSimilarityScores(properties, subject, newCalcOptions);
  addQualityTiers(properties);
  return properties;
};

const getLotAreaInSqft = (listing: ZillowPropertySearchResponse): AloftArea | undefined => {
  const area = listing.lotAreaValue;
  const unit = listing.lotAreaUnit;

  if (isNull(area) || isNull(unit)) return undefined;

  const cleanUnit = unit.trim().toLowerCase();
  if (cleanUnit === 'acres' || cleanUnit === 'acre') {
    return AloftArea.fromNumber(area, 'acre');
  }
  if (cleanUnit === 'square feet' || cleanUnit === 'sqft') {
    return AloftArea.fromNumber(area, 'sqft');
  }
  return undefined;
};
